import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiVisibilityReportRunsResponse } from '@brand24-original/llm-types';
import { getAiVisibilityRuns } from '../../services/ai-visibility-report/AiVisibilityReportService';
import { GET_AI_VISIBILITY_REPORTS, GET_AI_VISIBILITY_RUNS } from '../../query/ai-visibility-report/keys';

const POLL_MS = 10_000;

export const useGetAiVisibilityRuns = (projectId: number | null) => {
  const queryClient = useQueryClient();
  const query = useQuery<AiVisibilityReportRunsResponse>({
    queryKey: [GET_AI_VISIBILITY_RUNS, projectId],
    queryFn: () => getAiVisibilityRuns(projectId!),
    enabled: projectId != null,
    staleTime: 5_000,
    refetchOnWindowFocus: false,
    refetchInterval: (q) => {
      const data = q.state.data;
      if (data && data.activeRuns.length > 0) {
        return POLL_MS;
      }
      return false;
    },
  });

  const hadActiveRef = useRef(false);
  useEffect(() => {
    const hasActive = (query.data?.activeRuns.length ?? 0) > 0;
    if (hadActiveRef.current && !hasActive && projectId != null) {
      void queryClient.invalidateQueries({ queryKey: [GET_AI_VISIBILITY_REPORTS, projectId] });
    }
    hadActiveRef.current = hasActive;
  }, [query.data?.activeRuns.length, projectId, queryClient]);

  return query;
};
